import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import ClientAvatar from '../../../components/ClientAvatar';
import Button from '../../../components/ui/Button';

const MessageThread = ({ conversation, onSendMessage, sending = false }) => {
  const [message, setMessage] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const templates = [
    { id: 1, title: 'Market Update', content: `Hi {{client_name}}, I wanted to share some exciting market updates in your area. Home values have increased by 8% this quarter. Would you like to discuss what this means for your property?` },
    { id: 2, title: 'Check-in', content: `Hi {{client_name}}, just checking in to see how you're enjoying your new home! Is there anything I can help you with?` },
    { id: 3, title: 'Referral Request', content: `Hi {{client_name}}, I hope you're doing well! If you know anyone looking to buy or sell, I'd love to help them too. Thanks for thinking of me!` },
    { id: 4, title: 'Birthday Wishes', content: `Happy Birthday {{client_name}}! 🎉 Hope you have a wonderful day celebrating!` }
  ];

  useEffect(() => {
    setMessage('');
  }, [conversation?.id]);

  useEffect(() => {
    if (document.activeElement === textareaRef.current) return;
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [conversation?.messages, conversation?.id]);

  const handleSendMessage = () => {
    if (message?.trim()) {
      onSendMessage(message?.trim());
      setMessage('');
      setShowTemplates(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const handleTemplateSelect = (template) => {
    const personalizedContent = template?.content?.replace('{{client_name}}', conversation?.client?.name);
    setMessage(personalizedContent);
    setShowTemplates(false);
    textareaRef?.current?.focus();
  };

  const formatMessageTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'automated': return 'Bot';
      case 'agent': return 'User';
      default: return 'MessageSquare';
    }
  };

  const getDeliveryStatusIcon = (status) => {
    switch (status) {
      case 'sent': return 'Clock';
      case 'delivered': return 'Check';
      case 'read': return 'CheckCheck';
      case 'failed': return 'AlertCircle';
      default: return 'Clock';
    }
  };

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-background text-center p-8">
        <Icon name="MessageSquare" size={64} className="text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">Select a conversation</h3>
        <p className="text-muted-foreground max-w-md">
          Choose a conversation from the list to view messages and respond to your clients
        </p>
      </div>
    );
  }

  const characterCount = message?.length;
  const smsLimit = 160;
  const isOverSmsLimit = characterCount > smsLimit;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-3">
          <ClientAvatar
            src={conversation?.client?.avatar}
            name={conversation?.client?.name}
            size={40}
          />
          <div>
            <h3 className="font-semibold text-foreground">{conversation?.client?.name}</h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name={conversation?.channel === 'sms' ? 'MessageSquare' : 'Mail'} size={14} />
              <span className="capitalize">{conversation?.channel}</span>
              {conversation?.client?.phone && (
                <>
                  <span>•</span>
                  <span>{conversation?.client?.phone}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon">
            <Icon name="Phone" size={20} />
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="User" size={20} />
          </Button>
          <Button variant="ghost" size="icon">
            <Icon name="MoreVertical" size={20} />
          </Button>
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {conversation?.messages && conversation.messages.length > 0 ? (
          conversation.messages.map((msg) => (
            <div
              key={msg?.id}
              className={`flex ${msg?.isFromAgent ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-xs lg:max-w-md ${msg?.isFromAgent ? 'order-2' : 'order-1'}`}>
                <div
                  className={`rounded-lg px-4 py-2 ${
                    msg?.isFromAgent
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm">{msg?.content}</p>
                  
                  {msg?.attachment && (
                    <div className="mt-2 p-2 bg-black/10 rounded border">
                      <div className="flex items-center space-x-2">
                        <Icon name="Paperclip" size={16} />
                        <span className="text-xs">{msg?.attachment?.name}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className={`flex items-center space-x-2 mt-1 text-xs text-muted-foreground ${
                  msg?.isFromAgent ? 'justify-end' : 'justify-start'
                }`}>
                  {msg?.type && (
                    <Icon name={getMessageTypeIcon(msg?.type)} size={12} />
                  )}
                  <span>{formatMessageTime(msg?.timestamp)}</span>
                  {msg?.isFromAgent && (
                    <Icon 
                      name={getDeliveryStatusIcon(msg?.status)}
                      size={12}
                      className={msg?.status === 'read' ? 'text-primary' : 'text-muted-foreground'}
                    />
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md px-4">
              <Icon name="MessageSquare" size={48} className="text-muted-foreground mb-4 mx-auto" />
              <h3 className="text-lg font-medium text-foreground mb-2">No messages yet</h3>
              <p className="text-sm text-muted-foreground">
                Start the conversation by sending a message to {conversation?.client?.name || 'your client'}
              </p>
            </div>
          </div>
        )}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-muted text-foreground rounded-lg px-4 py-2 max-w-xs">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="border-t border-border bg-card p-4">
        {/* Templates */}
        {showTemplates && (
          <div className="mb-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">Quick Templates</h4>
              <Button variant="ghost" size="icon" onClick={() => setShowTemplates(false)}>
                <Icon name="X" size={16} />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {templates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="text-left p-3 bg-card rounded border hover:bg-accent/10 transition-smooth"
                >
                  <h5 className="text-sm font-medium text-foreground mb-1">{template?.title}</h5>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {template?.content?.replace('{{client_name}}', conversation?.client?.name)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-end space-x-3">
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowTemplates(!showTemplates)}
              className={showTemplates ? 'bg-accent' : ''}
            >
              <Icon name="FileText" size={20} />
            </Button>
            <Button variant="ghost" size="icon">
              <Icon name="Smile" size={20} />
            </Button>
            <Button variant="ghost" size="icon">
              <Icon name="Paperclip" size={20} />
            </Button>
          </div>
          
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              onKeyDown={handleKeyPress}
              placeholder={`Type a ${conversation?.channel} message...`}
              className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground text-base"
              rows={1}
              style={{ minHeight: '44px', maxHeight: '120px' }}
            />
            <div className="flex items-center justify-between mt-2">
              <div className={`text-xs ${isOverSmsLimit ? 'text-error' : 'text-muted-foreground'}`}>
                {conversation?.channel === 'sms' && (
                  <>
                    {characterCount}/{smsLimit} characters
                    {isOverSmsLimit && ' (will send as multiple messages)'}
                  </>
                )}
              </div>
            </div>
          </div>
          
          <Button
            type="button"
            onClick={handleSendMessage}
            disabled={!message?.trim() || sending}
            iconName="Send"
            iconPosition="right"
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;